import React, { useState, useEffect } from 'react'
import axios from 'axios'
import * as XLSX from "xlsx";
import OutletSearch from '../SelectSearch/OutletSearch'
import Navbar from '../Navbar'
import Footer from '../Footer'

const StockExpiry = () => {
    const [selectedOutlet, setSelectedOutlet] = useState("")
    const [stockData, setStockData] = useState([])
    const [loading, setLoading] = useState(false)
    const [showExpired, setShowExpired] = useState(false);

    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");

    useEffect(() => {
        const fetchStockData = async () => {
            if (!selectedOutlet) return

            setLoading(true)
            try {
                const response = await axios.get(`${baseUrl}/getstocklevels?outlet=${decodeURI(selectedOutlet)}`)

                setStockData(response.data.data || [])
            } catch (error) {
                console.error("Error fetching stock data:", error)
            } finally {
                setLoading(false)
            }
        }

        fetchStockData()
    }, [selectedOutlet, baseUrl])

    const filteredData = stockData
        .filter(item => {
            if (showExpired) {
                return item.stock_status === "Expired";
            }
            return item.stock_status !== "Expired";
        })
        .sort((a, b) => {
            const dateA = new Date(a.expiry_date);
            const dateB = new Date(b.expiry_date);

            if (!showExpired) {
                return dateA - dateB;
            }

            return dateB - dateA;
        });

    const handleExport = () => {
        if (filteredData.length === 0) return;

        const formattedRows = filteredData.map(item => ([
            item.name,
            item.qty,
            item.units,
            item.rate,
            item.total,
            new Date(item.expiry_date).toLocaleDateString(),
            item.days_until_expiry,
            item.stock_status
        ]));

        const sheetData = [
            [`Outlet: ${selectedOutlet || ""}`],
            [],
            [
                "Name",
                "Quantity",
                "Units",
                "Rate",
                "Total",
                "Expiry Date",
                `${showExpired ? "Expired Since" : "Days Left"}`,
                "Status"
            ],
            ...formattedRows
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

        worksheet["!cols"] = [
            { wch: 25 },
            { wch: 10 },
            { wch: 10 },
            { wch: 10 },
            { wch: 12 },
            { wch: 15 },
            { wch: 12 },
            { wch: 15 }
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Item List");

        XLSX.writeFile(
            workbook,
            `Item_List_${selectedOutlet || "Outlet"}_${showExpired ? "Expired" : "Active"}.xlsx`
        );
    };

    return (
        <div className='flex flex-col min-h-screen'>
            <Navbar />

            <div className='flex-grow'>
                <div className='max-w-6xl mx-auto p-4 bg-white rounded-lg shadow-lg my-2'>
                    <div className="flex justify-center">
                        <OutletSearch
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            token={token}
                        />
                    </div>

                    <div className="flex justify-between items-center -mt-3 mb-2">
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={showExpired}
                                onChange={(e) => setShowExpired(e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <label className="text-sm text-gray-700 font-medium">
                                View Expired Only
                            </label>
                        </div>

                        <button
                            onClick={handleExport}
                            disabled={filteredData.length === 0}
                            className={`px-3 py-1.5 text-sm rounded-md transition-colors
                                ${filteredData.length === 0
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-green-600 text-white hover:bg-green-700"
                                }`}
                        >
                            Export Excel
                        </button>
                    </div>

                    {loading ? (
                        <p>Loading...</p>
                    ) : (
                        <div className="overflow-x-auto border rounded-md max-h-[500px] overflow-y-auto tiny-scrollbar">
                            <table className="min-w-full text-sm text-center">
                                <thead>
                                    <tr className="bg-gray-100 sticky top-0 z-20">
                                        {["Name", "Qty", "Units", "Rate", "Total", "Expiry Date", `${showExpired ? "Expired Since" : "Days Left"}`, "Status"].map((h) => (
                                            <th
                                                key={h}
                                                className="px-3 py-2.5 text-gray-700 whitespace-nowrap font-semibold uppercase tracking-wider"
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody className="divide-y divide-gray-100">
                                    {filteredData.length > 0 ? (
                                        filteredData.map((item) => (
                                            <tr key={item.id} className="hover:bg-gray-50/60">
                                                <td className="px-3 py-2.5 font-medium text-gray-900 whitespace-nowrap">
                                                    {item.name}
                                                </td>

                                                <td className="px-3 py-2.5 text-gray-600">
                                                    {item.qty}
                                                </td>

                                                <td className="px-3 py-2.5 text-gray-600">
                                                    {item.units}
                                                </td>

                                                <td className="px-3 py-2.5 text-gray-600">
                                                    {item.rate}
                                                </td>

                                                <td className="px-3 py-2.5 font-medium text-gray-900">
                                                    {item.total}
                                                </td>

                                                <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                                                    {new Date(item.expiry_date).toLocaleDateString()}
                                                </td>

                                                <td className="px-3 py-2.5">
                                                    <span
                                                        className={`px-2 py-1 rounded-md text-xs font-medium ${item.days_until_expiry <= 5
                                                            ? "bg-red-50 text-red-600"
                                                            : item.days_until_expiry <= 15
                                                                ? "bg-yellow-50 text-yellow-600"
                                                                : "bg-green-50 text-green-600"
                                                            }`}
                                                    >
                                                        {item.days_until_expiry} {item.days_until_expiry === 1 || item.days_until_expiry === -1 ? "day" : "days"}
                                                    </span>
                                                </td>

                                                <td className="px-3 py-2.5">
                                                    <span
                                                        className={`px-2 py-1 rounded-md text-xs font-medium ${item.stock_status === "Expired"
                                                            ? "bg-red-100 text-red-700"
                                                            : item.stock_status === "Expiring Soon"
                                                                ? "bg-yellow-100 text-yellow-700"
                                                                : "bg-green-100 text-green-700"
                                                            }`}
                                                    >
                                                        {item.stock_status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center py-4 text-gray-600">
                                                No data found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    )
}

export default StockExpiry